/**
 * mobile-nav.js
 *
 * Controls the slide-in mobile navigation panel (visible < 768 px).
 *
 * Responsibilities:
 *  - Open / close (hamburger button, overlay click, X button, ESC key)
 *  - Focus trap while open (Tab / Shift+Tab cycle within panel)
 *  - Body scroll lock via data-scroll-lock on <html> (same token as cart-drawer.js)
 *  - aria-expanded on hamburger button; aria-hidden on panel
 *  - Returns focus to hamburger on close
 *
 * Pattern mirrors cart-drawer.js — same open/close/focus-trap/scroll-lock/Esc helpers.
 */

(function () {
  'use strict';

  const navEl = document.getElementById('mobile-nav');
  if (!navEl) return;

  const panelEl = navEl.querySelector('.mobile-nav__panel');
  const overlayEl = navEl.querySelector('[data-mobile-nav-overlay]');
  const closeBtn = navEl.querySelector('[data-mobile-nav-close]');
  const hamburgerBtn = document.querySelector('[aria-controls="mobile-nav"]');

  // ─── Open / close ──────────────────────────────────────────────────────────

  function openNav() {
    navEl.setAttribute('aria-hidden', 'false');
    navEl.classList.add('is-open');
    document.documentElement.setAttribute('data-scroll-lock', '');
    if (hamburgerBtn) hamburgerBtn.setAttribute('aria-expanded', 'true');
    // Shift focus into panel for focus-trap baseline
    requestAnimationFrame(function () { panelEl.focus(); });
  }

  function closeNav() {
    navEl.setAttribute('aria-hidden', 'true');
    navEl.classList.remove('is-open');
    document.documentElement.removeAttribute('data-scroll-lock');
    if (hamburgerBtn) {
      hamburgerBtn.setAttribute('aria-expanded', 'false');
      hamburgerBtn.focus();
    }
  }

  function isOpen() {
    return navEl.classList.contains('is-open');
  }

  // ─── Focus trap ────────────────────────────────────────────────────────────

  function getFocusable() {
    return Array.from(
      panelEl.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) { return !el.closest('[aria-hidden="true"]'); });
  }

  navEl.addEventListener('keydown', function (e) {
    if (!isOpen()) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeNav();
      return;
    }

    if (e.key === 'Tab') {
      var focusable = getFocusable();
      if (!focusable.length) { e.preventDefault(); return; }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  });

  // ─── Trigger bindings ──────────────────────────────────────────────────────

  if (hamburgerBtn) {
    hamburgerBtn.addEventListener('click', function () {
      if (isOpen()) { closeNav(); } else { openNav(); }
    });
  }

  if (overlayEl) overlayEl.addEventListener('click', closeNav);
  if (closeBtn) closeBtn.addEventListener('click', closeNav);

  // ─── Integrate with header-scroll.js ───────────────────────────────────────
  // header-scroll.js skips hiding the header when the cart drawer is open.
  // We apply the same data-scroll-lock mechanism so page scroll is blocked
  // while mobile nav is open. The header itself stays visible while the nav
  // panel is over it (full-screen overlay prevents scroll anyway).

})();
